import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  listOrgMembersApiv2,
  removeOrgMemberApi,
  updateOrgMemberApi,
  type ListOrgMembersResponseContent,
  type UpdateOrgMemberRequestContent
} from '../../org-members';

/**
 * TODO: CDK - Improve error responses across all member management endpoints
 * Many endpoints return generic 500 "Internal Server Error" instead of specific validation errors.
 * Should return 400/403/404 with descriptive messages for common issues like:
 * - Member not found
 * - User not authorized to manage members
 * - Invalid role assignment
 * - Cannot remove last admin
 */

/**
 * Grouped test component for all Member endpoints
 */
const TestMembers: React.FC = () => {
  // List Members state
  const [listMembersOrgId, setListMembersOrgId] = useState('');
  const [listMembersResponse, setListMembersResponse] = useState<ListOrgMembersResponseContent | null>(null);
  const [listMembersLoading, setListMembersLoading] = useState(false);
  const [listMembersError, setListMembersError] = useState<string | null>(null);

  // Update Member state
  const [updateOrgId, setUpdateOrgId] = useState('');
  const [updateUserId, setUpdateUserId] = useState('');
  const [updateRole, setUpdateRole] = useState('member');
  const [updateResponse, setUpdateResponse] = useState<any>(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  // Remove Member state
  const [removeOrgId, setRemoveOrgId] = useState('');
  const [removeUserId, setRemoveUserId] = useState('');
  const [removeResponse, setRemoveResponse] = useState<any>(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  // Handlers
  const handleListMembers = async () => {
    if (!listMembersOrgId.trim()) {
      setListMembersError('Please enter an organization ID');
      return;
    }

    setListMembersLoading(true);
    setListMembersError(null);

    try {
      const result = await listOrgMembersApiv2({ orgId: listMembersOrgId.trim() });
      setListMembersResponse(result);
    } catch (err) {
      setListMembersError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setListMembersLoading(false);
    }
  };

  const handleUpdateMember = async () => {
    if (!updateOrgId.trim() || !updateUserId.trim()) {
      setUpdateError('Please enter both organization ID and user ID');
      return;
    }

    setUpdateLoading(true);
    setUpdateError(null);

    try {
      const request: UpdateOrgMemberRequestContent = {
        orgId: updateOrgId.trim(),
        userId: updateUserId.trim(),
        role: updateRole as any
      };
      const result = await updateOrgMemberApi(request);
      setUpdateResponse(result);
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!removeOrgId.trim() || !removeUserId.trim()) {
      setRemoveError('Please enter both organization ID and user ID');
      return;
    }

    setRemoveLoading(true);
    setRemoveError(null);

    try {
      const result = await removeOrgMemberApi({
        orgId: removeOrgId.trim(),
        userId: removeUserId.trim()
      });
      setRemoveResponse(result);
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setRemoveLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#fff' }}>
        Member Management APIs
      </Typography>

      {/* List Members */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">List Organization Members</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID"
              value={listMembersOrgId}
              onChange={(e) => setListMembersOrgId(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleListMembers}
              disabled={listMembersLoading}
            >
              {listMembersLoading ? <CircularProgress size={24} /> : 'List Members'}
            </Button>
            {listMembersResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
                  Members ({Object.keys(listMembersResponse.members || {}).length}):
                </Typography>
                {listMembersResponse.members && Object.keys(listMembersResponse.members).length > 0 ? (
                  <List>
                    {Object.entries(listMembersResponse.members).map(([userId, member], index) => (
                      <React.Fragment key={userId}>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {member.orgEmail || 'No email'}
                                <Chip label={member.role} size="small" />
                                {member.customRoleName && (
                                  <Chip label={member.customRoleName} size="small" color="primary" />
                                )}
                              </Box>
                            }
                            secondary={
                              <>
                                User ID: {member.userId}
                                <br />
                                Joined: {member.joinedDate ? new Date(member.joinedDate).toLocaleString() : 'N/A'}
                                {member.userProfile && (
                                  <>
                                    <br />
                                    Name: {member.userProfile.firstName} {member.userProfile.lastName}
                                  </>
                                )}
                              </>
                            }
                          />
                        </ListItem>
                        {index < Object.keys(listMembersResponse.members).length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography>No members found</Typography>
                )}
              </Paper>
            )}
            {listMembersError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{listMembersError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Update Member */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Update Member Role</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID"
              value={updateOrgId}
              onChange={(e) => setUpdateOrgId(e.target.value)}
              fullWidth
            />
            <TextField
              label="User ID"
              value={updateUserId}
              onChange={(e) => setUpdateUserId(e.target.value)}
              fullWidth
            />
            <TextField
              label="New Role"
              value={updateRole}
              onChange={(e) => setUpdateRole(e.target.value)}
              fullWidth
              placeholder="admin, member, viewer, etc."
              helperText="Valid roles: primary_owner, admin, billing_admin, member, viewer, custom"
            />
            <Button
              variant="contained"
              onClick={handleUpdateMember}
              disabled={updateLoading}
            >
              {updateLoading ? <CircularProgress size={24} /> : 'Update Member'}
            </Button>
            {updateResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(updateResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {updateError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{updateError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Remove Member */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Remove Member</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID"
              value={removeOrgId}
              onChange={(e) => setRemoveOrgId(e.target.value)}
              fullWidth
            />
            <TextField
              label="User ID"
              value={removeUserId}
              onChange={(e) => setRemoveUserId(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleRemoveMember}
              disabled={removeLoading}
              color="error"
            >
              {removeLoading ? <CircularProgress size={24} /> : 'Remove Member'}
            </Button>
            {removeResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(removeResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {removeError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{removeError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default TestMembers;
